import { User } from 'react-feather';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Div } from '../../common/UI/Generic';
import { ExportsContext } from '../Exports/ExportsProvider';
import { Permission } from '../../__generated__/graphql';
import { useSession } from '../Auth/SessionProvider';
import UI from '../../common/UI';
import config from '../../config';
import useMediaDevice from '../../common/useMediaDevice';
import useOnClickOutside from '../../common/useOnClickOutside';
import useProject from '../useProject';

const UserNav = () => {
  const { t } = useTranslation();
  const { user, hasPermission } = useSession();
  const project = useProject();
  const device = useMediaDevice();

  const { pendingExportsCount, openExportsModal } = useContext(ExportsContext);

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectDropdown = useRef();

  useOnClickOutside(projectDropdown, () => setProjectDropdownOpen(false));

  const name = user.first_name || user.last_name || user.email;

  const hasExportsPermission = project && hasPermission(project.id, [Permission.exports]);
  const hasAdminPermission = project && hasPermission(project.id, [Permission.admin]);

  const showProjectNav = project || user.project_memberships.length > 0;

  return (
    <UserNavContainer>
      {showProjectNav && (
        <UserNavItem
          onClick={() => setProjectDropdownOpen((open) => !open)}
          ref={projectDropdown}
        >
          <UI.Dropdown sc={{ open: projectDropdownOpen, alignH: 'right', alignV: 'bottom' }}>
            {project && (hasExportsPermission || hasAdminPermission) && (
              <UI.Div>
                {hasExportsPermission && (
                  <UI.A onClick={() => openExportsModal()} role="button">
                    {t('nav.exports')}
                  </UI.A>
                )}
                {hasAdminPermission && (
                  <UI.NavLink to={`/${project.id}/project`}>
                    {t('nav.settings')}
                  </UI.NavLink>
                )}
              </UI.Div>
            )}
            {(!project || user.project_memberships.length > 1) && (
              <ProjectsContainer>
                <NavHeading>{t('nav.switch_project')}</NavHeading>
                {user.project_memberships.filter((membership) => membership.project.id !== project?.id)
                  .map(({ project: linkedProject }) => (
                    <UI.A
                      href={`${config.backendPath}/${linkedProject.id}`}
                      sc={{ noWrap: true }}
                      key={linkedProject.id}
                    >
                      {linkedProject.organisation_name.substr(0, 30)}
                      {linkedProject.organisation_name.length > 30 && <>&hellip;</>}
                    </UI.A>
                  ))}
              </ProjectsContainer>
            )}
            {project && (
              <UI.Div>
                <UI.NavLink to="/projects/add">
                  {t('nav.new_project')}
                </UI.NavLink>
              </UI.Div>
            )}
          </UI.Dropdown>
          <UI.Button sc={{ size: 'sm', brand: !device.isSmall ? 'secondary.400/70' : undefined }}>
            {pendingExportsCount > 0 && (
              <>
                <UI.Icon>
                  <UI.Loader sc={{ brand: 'secondary.300' }} />
                </UI.Icon>
                {' '}
                {t('nav.exporting', { count: pendingExportsCount })}
              </>
            )}
            {pendingExportsCount === 0 && (
              <>
                {project && project.organisation_name.substr(0, 18).trim()}
                {project && project.organisation_name.length > 18 && <>&hellip;</>}
                {!project && t('nav.projects')}
              </>
            )}
            {' '}
            <UI.Caret sc={{ color: 'white' }} />
          </UI.Button>
        </UserNavItem>
      )}

      <UI.AButton href="/me" sc={{ brand: !device.isSmall ? 'secondary.400/70' : undefined, size: 'sm' }}>
        <UI.Icon>
          <User />
        </UI.Icon>
        {' '}
        {name}
      </UI.AButton>
    </UserNavContainer>
  );
};

export const UserNavContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: ${13 / 16}rem;
    grid-gap: ${theme.gutter / 4}px;

    @media ${theme.devices.sm} {
      height: 60;
    }

    @media ${theme.devices.md} {
      height: ${theme.navBarHeight.md}px;
      margin-left: auto;
    }
  `}
`;

export const ProjectsContainer = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`;

const UserNavItem = styled(Div)`
  ${({ theme }) => css`
    position: relative;
    display: inline-block;

    ${UI.Dropdown} {
      min-width: 150px;
      max-width: 300px;
      color: ${theme.colors.gray[500]};
      background: ${theme.colors.gray[900]};
      border-radius: ${theme.borderRadiuses.md}px;

      > div {
        padding: 5px 0;
      }

      > div + div {
        border-top: 1px solid ${theme.colors.gray[800]};
      }

      ${NavHeading} {
        display: block;
        padding: ${theme.gutter / 4}px ${theme.gutter / 2}px;
      }

      ${UI.NavLink}, ${UI.Link}, ${UI.A} {
        display: block;
        padding: ${theme.gutter / 4}px ${theme.gutter / 2}px;
        line-height: 1.5;
        color ${theme.colors.white};

        &:hover {
          background: ${theme.colors.gray[800]};
        }
      }
    }
  `}
`;

const NavHeading = styled(UI.Span)``;

export default UserNav;
